/**
 * Clients — the agent's book of current customers.
 *
 * Agents see their own clients; agency principals see their agents';
 * KR admins see all (visibility enforced server-side). Clients are
 * created here directly or by converting a Won lead from the lead page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Users, Search, Plus, Loader2, Phone, Mail, Upload, FileSpreadsheet, Trash2, RotateCcw, Download } from 'lucide-react';
import { clientsAPI } from '@/lib/api';
import { getErrorMessage, formatPhone, formatPhoneInput } from '@/lib/utils';
import { isKRRole } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';
import RetentionView from '@/components/retention/RetentionView';
import RetentionKpis from '@/components/retention/RetentionKpis';
import AdminRetentionRules from '@/components/retention/AdminRetentionRules';
import NeedsAnalysisRules from '@/components/retention/NeedsAnalysisRules';

const PRODUCT_TYPES = ['Life', 'Ancillary', 'Medicare', 'Medicare Advantage', 'ACA', 'Dental', 'Vision', 'Hospital Indemnity', 'Accident', 'Annuity', 'Final Expense', 'Other'];
const PAGE_SIZES = [25, 50, 75, 100];

// Client avatar — reuses the auth-safe blob hook (the photo endpoint
// needs a Bearer token a plain <img> can't send). Falls back to initials.
function ClientAvatar({ client }) {
  const blobUrl = useInternalCoverBlob(client?.photo_url || null);
  const initials = `${(client?.first_name || '').charAt(0)}${(client?.last_name || '').charAt(0)}`.toUpperCase() || '?';
  return (
    <div className="h-10 w-10 rounded-full overflow-hidden bg-teal-600/10 grid place-items-center shrink-0 text-teal-800 font-semibold text-sm" data-testid="client-avatar">
      {blobUrl ? <img src={blobUrl} alt={initials} className="h-full w-full object-cover" /> : <span>{initials}</span>}
    </div>
  );
}

// Page-size selector + prev/next, mirroring the agents listing.
function PaginationBar({ page, pageSize, total, onPage, onPageSize, loading }) {
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);
  const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap mt-3" data-testid="clients-pagination">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <span>Show</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          data-testid="clients-page-size"
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>per page</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-500" data-testid="clients-range">{start}–{end} of {total}</span>
        <Button variant="outline" size="sm" disabled={loading || page <= 0} onClick={() => onPage(page - 1)} data-testid="clients-prev">Previous</Button>
        <Button variant="outline" size="sm" disabled={loading || page >= maxPage} onClick={() => onPage(page + 1)} data-testid="clients-next">Next</Button>
      </div>
    </div>
  );
}


export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const kr = isKRRole(user);
  const [tab, setTab] = useState('all');
  const [retentionRefresh, setRetentionRefresh] = useState(0);
  const bumpRetention = () => setRetentionRefresh((n) => n + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (q) params.q = q;
      if (productFilter) params.product = productFilter;
      if (statusFilter) params.status = statusFilter;
      if (tab === 'trash') params.archived = true;
      const r = await clientsAPI.list(params);
      setRows(r.data?.clients || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load clients'));
    } finally { setLoading(false); }
  }, [q, productFilter, statusFilter, tab, pageSize, page, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  // Reset to the first page whenever the filters or tab change.
  useEffect(() => { setPage(0); }, [q, productFilter, statusFilter, tab, pageSize, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  };

  const restoreClient = async (e, id) => {    e.stopPropagation();
    try {
      await clientsAPI.restore(id);
      toast.success('Client restored');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not restore the client'));
    }
  };

  const exportClients = async () => {
    setExporting(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (productFilter) params.product = productFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await clientsAPI.exportCsv(params);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Export failed'));
    } finally { setExporting(false); }
  };


  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="clients-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-teal-600/10 grid place-items-center">
            <Users className="h-5 w-5 text-teal-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
            <p className="text-sm text-slate-500">Your current book of business</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportClients} disabled={exporting} data-testid="export-clients-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export
          </Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-clients-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Import
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Client
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex-wrap h-auto" data-testid="clients-tabs">
          <TabsTrigger value="all" data-testid="tab-all-clients">All Clients</TabsTrigger>
          <TabsTrigger value="renewals" data-testid="tab-renewals">Retention &amp; Renewals</TabsTrigger>
          <TabsTrigger value="cross_sell" data-testid="tab-cross-sell">Cross-Sell</TabsTrigger>
          <TabsTrigger value="needs_contact" data-testid="tab-needs-contact">Needs Contact</TabsTrigger>
          <TabsTrigger value="at_risk" data-testid="tab-at-risk">At Risk</TabsTrigger>
          {kr && <TabsTrigger value="admin_rules" data-testid="tab-admin-rules">Admin Rules</TabsTrigger>}
          <TabsTrigger value="trash" data-testid="tab-trash"><Trash2 size={13} className="mr-1" /> Trash</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, phone, or email…"
                className="pl-9"
                data-testid="clients-search"
              />
            </div>
            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 sm:w-48"
              data-testid="clients-product-filter"
            >
              <option value="">All products</option>
              {PRODUCT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 sm:w-40"
              data-testid="clients-status-filter"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/60 text-xs" data-testid="clients-sort-bar">
                <span className="text-slate-400 mr-1">Sort by:</span>
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'date', label: 'Date' },
                  { key: 'product', label: 'Product' },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggleSort(s.key)}
                    data-testid={`clients-sort-${s.key}`}
                    className={`rounded-full px-2.5 py-1 border transition-colors ${sortBy === s.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  >
                    {s.label}{sortBy === s.key ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : ''}
                  </button>
                ))}
              </div>
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : rows.length === 0 ? (
                <div className="text-center py-16 text-slate-500" data-testid="clients-empty">
                  <Users className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  No clients yet. Add one, or convert a Won lead.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {rows.map((c) => (
                    <li
                      key={c.client_id}
                      onClick={() => navigate(`/clients/${c.client_id}`)}
                      className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                      data-testid={`client-row-${c.client_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ClientAvatar client={c} />
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900">
                            {c.first_name} {c.last_name || ''}
                            {c.source_lead_id && <Badge variant="outline" className="ml-2 text-[10px]">from lead</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            {c.phone && <span className="flex items-center gap-1"><Phone size={11} /> {formatPhone(c.phone)}</span>}
                            {c.email && <span className="flex items-center gap-1 truncate"><Mail size={11} /> {c.email}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {(c.products || []).slice(0, 3).map((p, i) => (
                          <Badge key={i} className="bg-teal-50 text-teal-700 border-teal-200 text-[10px]">{p.product_type}</Badge>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <PaginationBar page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} loading={loading} />
        </TabsContent>

        <TabsContent value="renewals" className="mt-4">
          <RetentionKpis onSelect={setTab} refreshKey={`renewals:${retentionRefresh}`} />
          <RetentionView category="renewals" onChanged={bumpRetention} />
        </TabsContent>
        <TabsContent value="cross_sell" className="mt-4">
          <RetentionKpis onSelect={setTab} refreshKey={`cross_sell:${retentionRefresh}`} />
          <RetentionView category="cross_sell" onChanged={bumpRetention} />
        </TabsContent>
        <TabsContent value="needs_contact" className="mt-4">
          <RetentionKpis onSelect={setTab} refreshKey={`needs_contact:${retentionRefresh}`} />
          <RetentionView category="needs_contact" onChanged={bumpRetention} />
        </TabsContent>
        <TabsContent value="at_risk" className="mt-4">
          <RetentionKpis onSelect={setTab} refreshKey={`at_risk:${retentionRefresh}`} />
          <RetentionView category="at_risk" onChanged={bumpRetention} />
        </TabsContent>
        {kr && (
          <TabsContent value="admin_rules" className="mt-4 space-y-4">
            <AdminRetentionRules />
            <NeedsAnalysisRules />
          </TabsContent>
        )}

        <TabsContent value="trash" className="mt-4">
          <p className="text-sm text-slate-500 mb-3">Deleted clients are kept here. Restore one if you removed it by mistake.</p>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : rows.length === 0 ? (
                <div className="text-center py-16 text-slate-500" data-testid="clients-trash-empty">
                  <Trash2 className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  Trash is empty.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {rows.map((c) => (
                    <li
                      key={c.client_id}
                      className="px-4 py-3 flex items-center justify-between hover:bg-slate-50"
                      data-testid={`trash-client-row-${c.client_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{c.first_name} {c.last_name || ''}</div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          {c.phone && <span className="flex items-center gap-1"><Phone size={11} /> {formatPhone(c.phone)}</span>}
                          {c.email && <span className="flex items-center gap-1 truncate"><Mail size={11} /> {c.email}</span>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => restoreClient(e, c.client_id)}
                        data-testid={`restore-client-btn-${c.client_id}`}
                      >
                        <RotateCcw size={14} className="mr-1.5" /> Restore
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateClientDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setShowCreate(false); navigate(`/clients/${id}`); }}
      />

      <ImportClientsDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onDone={() => { setShowImport(false); load(); }}
      />
    </div>
  );
}

function CreateClientDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', product_type: '', address: '', city: '', state: '', zip_code: '' });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        zip_code: form.zip_code.trim() || undefined,
        products: form.product_type ? [{ product_type: form.product_type }] : [],
      };
      const r = await clientsAPI.create(payload);
      toast.success('Client added');
      onCreated(r.data.client_id);
      setForm({ first_name: '', last_name: '', phone: '', email: '', product_type: '', address: '', city: '', state: '', zip_code: '' });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create client'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-client-dialog">
        <DialogHeader><DialogTitle>Add Client</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Add a new client to your book of business.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">First name *</label>
              <Input value={form.first_name} onChange={(e) => upd('first_name', e.target.value)} data-testid="client-first-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Last name</label>
              <Input value={form.last_name} onChange={(e) => upd('last_name', e.target.value)} data-testid="client-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Phone</label>
              <Input value={formatPhoneInput(form.phone)} onChange={(e) => upd('phone', e.target.value)} data-testid="client-phone" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Email</label>
              <Input value={form.email} onChange={(e) => upd('email', e.target.value)} data-testid="client-email" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Product (optional)</label>
            <select
              value={form.product_type}
              onChange={(e) => upd('product_type', e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="client-product-type"
            >
              <option value="">— none —</option>
              {PRODUCT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Address</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => upd('address', v)}
              onPlaceSelected={(parsed) => setForm((p) => ({
                ...p,
                address: parsed.street || parsed.formatted || p.address,
                city: parsed.city || p.city,
                state: parsed.state || p.state,
                zip_code: parsed.zip || p.zip_code,
              }))}
              testId="create-client-address-autocomplete"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">City</label>
              <Input value={form.city} onChange={(e) => upd('city', e.target.value)} data-testid="client-city" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">State</label>
              <Input value={form.state} onChange={(e) => upd('state', e.target.value)} data-testid="client-state" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">ZIP</label>
              <Input value={form.zip_code} onChange={(e) => upd('zip_code', e.target.value)} data-testid="client-zip" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="client-create-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Best-effort auto-match of a spreadsheet column to a target field.
function autoMatch(fields, columns) {
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const map = {};
  fields.forEach((f) => {
    const targets = [norm(f.key), norm(f.label)];
    const hit = columns.find((c) => targets.includes(norm(c)));
    if (hit) map[f.key] = hit;
  });
  return map;
}

function ImportClientsDialog({ open, onClose, onDone }) {
  const [step, setStep] = useState('upload'); // upload | map
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [fields, setFields] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState('');
  const [dupMode, setDupMode] = useState('skip'); // skip | update

  const reset = () => {
    setStep('upload'); setParsing(false); setCommitting(false);
    setFields([]); setColumns([]); setRows([]); setMapping({}); setFileName(''); setDupMode('skip');
  };

  const handleClose = () => { reset(); onClose(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await clientsAPI.importPreview(fd);
      const cols = r.data?.columns || [];
      const flds = r.data?.fields || [];
      setColumns(cols);
      setRows(r.data?.rows || []);
      setFields(flds);
      setMapping(autoMatch(flds, cols));
      setStep('map');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not read file'));
    } finally {
      setParsing(false);
      e.target.value = '';
    }
  };

  const commit = async () => {
    if (!mapping.first_name) { toast.error('Map a column to First name'); return; }
    setCommitting(true);
    try {
      const r = await clientsAPI.importCommit({ mapping, rows, duplicate_mode: dupMode });
      const { created = 0, updated = 0, skipped = 0, duplicates = 0 } = r.data || {};
      const extras = [];
      if (updated) extras.push(`${updated} updated`);
      if (duplicates) extras.push(`${duplicates} duplicate${duplicates === 1 ? '' : 's'} skipped`);
      if (skipped) extras.push(`${skipped} incomplete skipped`);
      toast.success(`Imported ${created} client${created === 1 ? '' : 's'}${extras.length ? ` — ${extras.join(', ')}` : ''}`);
      reset();
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Import failed'));
    } finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl" data-testid="import-clients-dialog">
        <DialogHeader><DialogTitle>Import Clients</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Upload an Excel or CSV file and map its columns to client fields.</DialogDescription>

        {step === 'upload' && (
          <div className="py-6">
            <label
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-10 cursor-pointer hover:border-teal-400 hover:bg-teal-50/40 transition-colors"
              data-testid="import-dropzone"
            >
              {parsing ? (
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              ) : (
                <FileSpreadsheet className="h-8 w-8 text-slate-400" />
              )}
              <span className="text-sm font-medium text-slate-700">
                {parsing ? 'Reading file…' : 'Click to choose an Excel or CSV file'}
              </span>
              <span className="text-xs text-slate-400">.xlsx, .xls or .csv</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={onFile}
                data-testid="import-file-input"
              />
            </label>
          </div>
        )}

        {step === 'map' && (
          <div className="py-2">
            <p className="text-sm text-slate-600 mb-3">
              <span className="font-medium text-slate-800">{fileName}</span> — {rows.length} row{rows.length === 1 ? '' : 's'}. Match each field to a column.
            </p>
            <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
              {fields.map((f) => (
                <div key={f.key} className="grid grid-cols-2 gap-3 items-center">
                  <label className="text-sm text-slate-700">
                    {f.label}{f.required && <span className="text-red-500"> *</span>}
                  </label>
                  <select
                    value={mapping[f.key] || ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    data-testid={`import-map-${f.key}`}
                  >
                    <option value="">— skip —</option>
                    {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="text-xs font-medium text-slate-600 mb-1.5">If a client already exists (same phone or email)</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDupMode('skip')}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm text-left transition-colors ${dupMode === 'skip' ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid="import-dupmode-skip"
                >
                  <span className="font-medium">Skip duplicates</span>
                  <span className="block text-xs text-slate-400">Leave existing clients untouched</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDupMode('update')}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm text-left transition-colors ${dupMode === 'update' ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid="import-dupmode-update"
                >
                  <span className="font-medium">Update existing</span>
                  <span className="block text-xs text-slate-400">Overwrite fields with the file's data</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'map' && (
            <Button variant="ghost" onClick={() => setStep('upload')} data-testid="import-back-btn">Back</Button>
          )}
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          {step === 'map' && (
            <Button onClick={commit} disabled={committing} data-testid="import-commit-btn">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${rows.length} client${rows.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
